import { MOCK_LISTINGS, type Listing } from '@/app/lib/mock-db';
import { getStoredAccessToken } from '@/lib/auth/session';

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

export interface CarListingFilters {
  make_id?: string;
  model_id?: string;
  car_type_id?: string;
  fuel_type?: string;
  transmission?: string;
  year_min?: number;
  year_max?: number;
  price_min?: number;
  price_max?: number;
  city?: string;
  country_code?: string;
  search?: string;
}

function resolveImageUrl(raw: unknown, listingId: string): string {
  const fallback = `https://picsum.photos/seed/${encodeURIComponent(`listing-${listingId}`)}/900/600`;
  if (!raw || typeof raw !== 'string') return fallback;
  const value = raw.trim();
  if (!value) return fallback;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  if (value.startsWith('/')) return `${API}${value}`;
  return fallback;
}

function pickRawImage(row: any): string | null {
  if (row?.image_url && typeof row.image_url === 'string') return row.image_url;
  if (!Array.isArray(row?.images) || row.images.length === 0) return null;
  const primary = row.images.find((img: any) => img?.is_primary) || row.images[0];
  return primary?.image_url || primary?.image || null;
}

function normalizeListing(row: any, index: number): Listing | null {
  const idRaw = row?.id;
  const id = idRaw !== undefined && idRaw !== null ? String(idRaw) : '';
  if (!id) return null;

  const title = String(row?.title || `إعلان سيارة ${index + 1}`).trim();
  const description = String(row?.description || '').trim();
  const makeId = row?.make_id !== undefined && row?.make_id !== null ? String(row.make_id) : '';
  const modelId = row?.model_id !== undefined && row?.model_id !== null ? String(row.model_id) : '';
  const createdAt = String(row?.created_at || new Date().toISOString());
  const priceNum = Number(row?.price ?? 0);
  const yearNum = Number(row?.year ?? new Date().getFullYear());
  const imageRaw = pickRawImage(row);

  return {
    id,
    title,
    price: Number.isFinite(priceNum) ? priceNum : 0,
    year: Number.isFinite(yearNum) ? yearNum : new Date().getFullYear(),
    make_id: makeId,
    model_id: modelId,
    fuel_type: String(row?.fuel_type || '').trim() || undefined,
    transmission: String(row?.transmission || '').trim() || undefined,
    description,
    image_url: resolveImageUrl(imageRaw, id),
    created_at: createdAt,
  };
}

function applyLocalFilters(listings: Listing[], filters: CarListingFilters): Listing[] {
  return listings.filter((l) => {
    const searchText = String(filters.search || '').trim().toLowerCase();
    const listingText = `${l.title} ${l.description} ${l.make_id} ${l.model_id} ${l.year}`.toLowerCase();
    const matchesMake = !filters.make_id || l.make_id === filters.make_id;
    const matchesModel = !filters.model_id || l.model_id === filters.model_id;
    const matchesFuel = !filters.fuel_type || String(l.fuel_type || '') === filters.fuel_type;
    const matchesTransmission = !filters.transmission || String(l.transmission || '') === filters.transmission;
    const matchesYearMin = !filters.year_min || l.year >= filters.year_min;
    const matchesYearMax = !filters.year_max || l.year <= filters.year_max;
    const matchesPriceMin = !filters.price_min || l.price >= filters.price_min;
    const matchesPriceMax = !filters.price_max || l.price <= filters.price_max;
    const matchesSearch = !searchText || listingText.includes(searchText);
    return matchesMake && matchesModel && matchesFuel && matchesTransmission && matchesYearMin && matchesYearMax && matchesPriceMin && matchesPriceMax && matchesSearch;
  });
}

function buildHeaders(): HeadersInit {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getStoredAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (typeof window !== 'undefined') {
    const countryCode = (localStorage.getItem('app_country_code') || '').trim().toUpperCase();
    if (countryCode) headers['X-Country-Code'] = countryCode;
  }
  return headers;
}

export async function fetchCarListings(filters: CarListingFilters = {}): Promise<Listing[]> {
  const params = new URLSearchParams();
  if (filters.make_id) params.set('make_id', filters.make_id);
  if (filters.model_id) params.set('model_id', filters.model_id);
  if (filters.car_type_id) params.set('car_type_id', filters.car_type_id);
  if (filters.fuel_type) params.set('fuel_type', filters.fuel_type);
  if (filters.transmission) params.set('transmission', filters.transmission);
  if (filters.year_min) params.set('year_min', String(filters.year_min));
  if (filters.year_max) params.set('year_max', String(filters.year_max));
  if (filters.price_min) params.set('price_min', String(filters.price_min));
  if (filters.price_max) params.set('price_max', String(filters.price_max));
  if (filters.city) params.set('city', filters.city);
  if (filters.country_code) params.set('country_code', filters.country_code);
  if (filters.search) params.set('search', filters.search);

  const query = params.toString();
  const url = `${API}/api/marketplace/listings/${query ? `?${query}` : ''}`;

  try {
    const res = await fetch(url, { cache: 'no-store', headers: buildHeaders() });
    if (!res.ok) return applyLocalFilters(MOCK_LISTINGS, filters);
    const payload = await res.json();
    const rows = Array.isArray(payload) ? payload : (payload?.results || []);
    const normalized = rows
      .map(normalizeListing)
      .filter((l: Listing | null): l is Listing => Boolean(l));
    return normalized;
  } catch {
    return applyLocalFilters(MOCK_LISTINGS, filters);
  }
}

export async function fetchCarListingById(id: string): Promise<Listing | null> {
  if (!id) return null;
  try {
    const res = await fetch(`${API}/api/marketplace/listings/${id}/`, {
      cache: 'no-store',
      headers: buildHeaders(),
    });
    if (!res.ok) return null;
    const payload = await res.json();
    return normalizeListing(payload, 0);
  } catch {
    return null;
  }
}
